import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon, CheckIcon } from '@heroicons/react/24/outline';
import { fromNullable } from 'maybeasy';
import { assertNever } from '../../../../../Decoders';
import { emptyFragment } from '../../../../../EmptyFragment';
import { T } from '../../../../../Translations';
import { useClickOutside } from '../../../../DashboardSummary/useClickOutside';
import { LicenseOption, LicenseOptionStatus, usePreviewLicense } from './PreviewLicenseContext';

// DVLP-7601: the license dropdown in the preview bar. A card-styled trigger
// (the bar supplies the "License" label) opening a popover that groups the
// org's held PLTs first (Active, plus Pending ones flagged with a chip), then
// hides commercially-available PLTs the org doesn't hold behind an expander
// so the initial list stays short. When the expander opens, the popover (not
// the page) scrolls so the newly revealed rows sit in view. On first
// successful load the top held PLT is auto-selected (backend sorts by
// unlocks_count desc).

// Held licenses (Active or Pending) sit in the participants group; only
// unheld commercially-available PLTs go behind the expander.
const isHeld = (option: LicenseOption): boolean => {
  switch (option.status) {
    case 'Active':
    case 'Pending':
      return true;
    case 'Available':
      return false;
  }
  return assertNever(option.status);
};

function StatusPill({ status }: { status: LicenseOptionStatus }) {
  const label = (): JSX.Element | null => {
    switch (status) {
      case 'Active':
        return null;
      case 'Pending':
        return <T kind="Pending" />;
      case 'Available':
        return <T kind="Unlicensed" />;
    }
    return assertNever(status);
  };
  const text = label();
  if (text === null) return null;
  return (
    <span
      className="inline-flex items-center rounded-full border border-border bg-card text-muted-foreground px-2 py-0.5"
      style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-medium)' }}
      data-intercom-target={`preview-license-status-${status.toLowerCase()}`}
    >
      {text}
    </span>
  );
}

interface RowProps {
  option: LicenseOption;
  isSelected: boolean;
  onSelect: (id: number) => void;
}

function OptionRow({ option, isSelected, onSelect }: RowProps) {
  return (
    <li role="option" aria-selected={isSelected}>
      <button
        type="button"
        onClick={() => onSelect(option.product_license_type_id)}
        className={`w-full flex items-start gap-3 px-3 py-2.5 hover:bg-muted focus:outline-none focus:bg-muted text-left ${
          isSelected ? 'bg-primary/10' : ''
        }`}
        data-intercom-target={`preview-license-option-${option.product_license_type_id}`}
      >
        <span className="w-4 pt-1 flex-shrink-0">
          {isSelected && <CheckIcon className="w-4 h-4 text-primary" />}
        </span>
        <span className="flex-1 min-w-0">
          <span className="flex items-center gap-2">
            <span
              className="truncate flex-1 min-w-0"
              style={{ fontWeight: 'var(--font-weight-semibold)' }}
              title={option.name}
            >
              {option.name}
            </span>
            <StatusPill status={option.status} />
          </span>
          <span
            className="block text-muted-foreground mt-0.5"
            style={{ fontSize: 'var(--text-xs)' }}
          >
            <T
              kind="Unlocks {{count}} of {{total}} courses"
              count={option.unlocks_count}
              total={option.total_count}
            />
          </span>
        </span>
      </button>
    </li>
  );
}

function PreviewLicenseSelector() {
  const { selectedPltId, selectedOption, setSelectedPltId, optionsState, options } =
    usePreviewLicense();
  const [isOpen, setIsOpen] = useState(false);
  const [showUpsell, setShowUpsell] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const expanderRef = useRef<HTMLButtonElement>(null);

  const { heldOptions, availableOptions } = useMemo(
    () => ({
      heldOptions: options.filter(isHeld),
      availableOptions: options.filter((o) => !isHeld(o)),
    }),
    [options],
  );

  // Auto-pick the top-of-list PLT (already sorted by unlocks_count desc) on
  // first successful load if nothing is selected yet. Prefer a held PLT;
  // otherwise the first unheld option so the trigger doesn't stay empty for
  // orgs with no core PLT held.
  useEffect(() => {
    if (optionsState.kind !== 'ready' || selectedPltId.isJust()) return;
    fromNullable(heldOptions[0])
      .orElse(() => fromNullable(options[0]))
      .do((first) => setSelectedPltId(first.product_license_type_id));
  }, [optionsState.kind, options, heldOptions, selectedPltId, setSelectedPltId]);

  // Close popover on outside click via the shared hook. Escape is kept as a
  // small inline effect — no reusable equivalent exists in the codebase.
  const closePopover = useCallback(() => setIsOpen(false), []);
  const clickOutsideRefs = useMemo(() => [containerRef], []);
  useClickOutside(clickOutsideRefs, isOpen, closePopover);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen]);

  // When the expander opens, scroll the popover's own scroll container so the
  // expander sits at the top and the revealed rows are in view. Scrolling the
  // container directly (rather than scrollIntoView) keeps the page itself
  // from moving — scrollIntoView walks every scrollable ancestor, including
  // the document. The scroll is deferred one frame so the rows have rendered
  // and the scroll range exists.
  const toggleUpsell = () => {
    const opening = !showUpsell;
    setShowUpsell(opening);
    if (!opening) return;
    window.requestAnimationFrame(() => {
      const popover = popoverRef.current;
      const expander = expanderRef.current;
      if (!popover || !expander) return;
      popover.scrollTo({ top: expander.offsetTop, behavior: 'smooth' });
    });
  };

  const currentId = selectedOption.map((o) => o.product_license_type_id);
  const isCurrent = (option: LicenseOption) =>
    currentId.map((id) => id === option.product_license_type_id).getOrElseValue(false);

  const placeholder = (): string => {
    switch (optionsState.kind) {
      case 'loading':
        return '…';
      case 'errored':
        return '—';
      case 'ready':
        return '';
    }
    return assertNever(optionsState);
  };

  const disabled = optionsState.kind !== 'ready' || options.length === 0;

  const handleSelect = (id: number) => {
    setSelectedPltId(id);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className="relative flex-shrink-0">
      <button
        type="button"
        className="flex items-center gap-2 rounded-md bg-card text-foreground border border-border px-3 py-2 hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
        style={{
          fontSize: 'var(--text-base)',
          minWidth: '18rem',
          maxWidth: '28rem',
        }}
        onClick={() => setIsOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        data-intercom-target="preview-license-selector"
        title={selectedOption.map((o) => o.name).getOrElseValue('')}
      >
        <span className="flex-1 text-left truncate">
          {selectedOption.map((o) => o.name).getOrElse(placeholder)}
        </span>
        {selectedOption
          .map((o) => <StatusPill key="status" status={o.status} />)
          .getOrElse(emptyFragment)}
        {isOpen ? (
          <ChevronUpIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        ) : (
          <ChevronDownIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        )}
      </button>

      {isOpen && (
        <div
          ref={popoverRef}
          className="absolute left-0 z-50 mt-1 max-h-[480px] overflow-y-auto rounded-md bg-card text-foreground border border-border shadow-lg py-1"
          style={{ minWidth: '26rem', maxWidth: '32rem' }}
          data-intercom-target="preview-license-popover"
        >
          {heldOptions.length > 0 && (
            <>
              <div
                className="px-3 pt-2 pb-1 text-muted-foreground"
                style={{
                  fontSize: 'var(--text-xs)',
                  fontWeight: 'var(--font-weight-medium)',
                  letterSpacing: '0.05em',
                }}
                data-intercom-target="preview-license-active-header"
              >
                <T kind="AVAILABLE FOR PARTICIPANTS" />
              </div>
              <ul role="listbox" data-intercom-target="preview-license-active-list">
                {heldOptions.map((opt) => (
                  <OptionRow
                    key={opt.product_license_type_id}
                    option={opt}
                    isSelected={isCurrent(opt)}
                    onSelect={handleSelect}
                  />
                ))}
              </ul>
            </>
          )}

          {availableOptions.length > 0 && (
            <>
              <div className="border-t border-border my-1" />
              <button
                ref={expanderRef}
                type="button"
                onClick={toggleUpsell}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 text-primary hover:bg-muted focus:outline-none focus:bg-muted sticky top-0 bg-card z-10"
                style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}
                aria-expanded={showUpsell}
                data-intercom-target="preview-license-upsell-expander"
              >
                {showUpsell ? (
                  <ChevronUpIcon className="w-4 h-4" />
                ) : (
                  <ChevronDownIcon className="w-4 h-4" />
                )}
                {showUpsell ? (
                  <T kind="Hide other license options" />
                ) : (
                  <T kind="Show other license options" />
                )}
              </button>
              {showUpsell && (
                <>
                  <div
                    className="px-3 pt-1 pb-1 text-muted-foreground"
                    style={{
                      fontSize: 'var(--text-xs)',
                      fontWeight: 'var(--font-weight-medium)',
                      letterSpacing: '0.05em',
                    }}
                    data-intercom-target="preview-license-upsell-header"
                  >
                    <T kind="NOT INCLUDED IN YOUR CURRENT LICENSES" />
                  </div>
                  <ul role="listbox" data-intercom-target="preview-license-upsell-list">
                    {availableOptions.map((opt) => (
                      <OptionRow
                        key={opt.product_license_type_id}
                        option={opt}
                        isSelected={isCurrent(opt)}
                        onSelect={handleSelect}
                      />
                    ))}
                  </ul>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default PreviewLicenseSelector;
